import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { TeamAPI } from "../../services/api";
import CheckedImg from "../../assets/Checked.png";
import UnCheckedImg from "../../assets/UnChecked.png";

// Import status components
import InviteTeamMateStatus from "./team-status/InviteTeamMateStatus";
import ConfirmTeamMateStatus from "./team-status/ConfirmTeamMateStatus";
import AwaitingConfirmationStatus from "./team-status/AwaitingConfirmationStatus";
import SignTermsConditionsStatus from "./team-status/SignTermsConditionsStatus";
import SubmitForApprovalStatus from "./team-status/SubmitForApprovalStatus";
import AwaitingAdminApprovalStatus from "./team-status/AwaitingAdminApprovalStatus";
import ReadyForPaymentStatus from "./team-status/ReadyForPaymentStatus";

interface TeamMember {
  id: number;
  user_id: number;
  member_position: string;
  full_name: string;
  personal_email: string;
  user?: {
    id: number;
    full_name: string;
    email: string;
  };
}

interface Team {
  id: number;
  team_id: string;
  team_name: string;
  team_story?: string;
  league: {
    id: number;
    name: string;
  };
  category: {
    id: number;
    name: string;
  };
  members: TeamMember[];
  year: number;
  team_status: string;
  payment_status: string;
  profile_status: string;
  acknowledgment_status: string;
  member_count: number;
  is_complete: boolean;
  statusCheckpoints: {
    [key: string]: {
      name: string;
      completed: boolean;
    };
  };
  user_position: string;
  created_at: string;
  updated_at: string;
}

interface ChecklistItem {
  id: string;
  label: string;
  completed: boolean;
}

const TeamDetailsPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    if (teamId) {
      loadTeamDetails();
    }
  }, [teamId]);

  const loadTeamDetails = async () => {
    try {
      setLoading(true);
      setError("");

      // Get current user info
      const userInfo = JSON.parse(localStorage.getItem("user") || "{}");
      setCurrentUser(userInfo);

      // Load team details
      const response = await TeamAPI.getTeamDetails(teamId!);

      if (response.success) {
        const teamData = response.team;

        // Check if current user is a member of this team
        const isTeamMember = teamData.members.some(
          (member: TeamMember) => member.user_id === userInfo.id
        );

        if (!isTeamMember) {
          // Redirect to team list if not authorized
          navigate("/dashboard/team");
          return;
        }

        setTeam(teamData);
      } else {
        setError("Failed to load team details");
      }
    } catch (err: any) {
      console.error("Error loading team details:", err);
      setError(err.message || "Failed to load team details");
      // Redirect to team list on error
      navigate("/dashboard/team");
    } finally {
      setLoading(false);
    }
  };

  const getChecklistItems = (): ChecklistItem[] => {
    if (!team) return [];

    return [
      {
        id: "eligible_league",
        label: "Eligible League",
        completed: team.statusCheckpoints?.eligible_league?.completed || false,
      },
      {
        id: "eligible_category",
        label: "Eligible Category",
        completed:
          team.statusCheckpoints?.eligible_category?.completed || false,
      },
      {
        id: "confirm_team_member",
        label: "Confirm your team member",
        completed:
          team.statusCheckpoints?.team_member_confirmation?.completed || false,
      },
      {
        id: "sign_terms",
        label: "Sign Acknowledgement",
        completed: team.statusCheckpoints?.terms_conditions?.completed || false,
      },
      {
        id: "submit_approval",
        label: "Submit for approval",
        completed: team.statusCheckpoints?.submit_approval?.completed || false,
      },
      {
        id: "confirmed_approval",
        label: "Confirmed for approval",
        completed:
          team.statusCheckpoints?.confirmed_approval?.completed || false,
      },
      {
        id: "complete_payment",
        label: "Complete payment",
        completed: team.statusCheckpoints?.complete_payment?.completed || false,
      },
    ];
  };

  const getCurrentTeamStatus = () => {
    if (!team || !currentUser) return "invite";

    const userPosition = team.members.find(
      (m) => m.user_id === currentUser.id
    )?.member_position;

    // Case 1: Team needs second member
    if (team.member_count < 2) {
      return "invite";
    }

    // Case 2: Member 2 is waiting for confirmation from member 1
    if (
      team.member_count === 2 &&
      !team.statusCheckpoints?.team_member_confirmation?.completed
    ) {
      if (userPosition === "1") {
        return "confirm"; // Member 1 sees confirmation interface
      } else {
        return "waiting"; // Member 2 sees waiting interface
      }
    }

    // Case 3: Team is complete but needs Terms & Conditions
    if (
      team.member_count === 2 &&
      team.statusCheckpoints?.team_member_confirmation?.completed
    ) {
      if (!team.statusCheckpoints?.terms_conditions?.completed) {
        return "terms";
      }

      // Case 4: Terms completed but not submitted for approval yet
      if (
        team.statusCheckpoints?.terms_conditions?.completed &&
        !team.statusCheckpoints?.submit_approval?.completed
      ) {
        return "submit";
      }

      // Case 5: Submitted for approval but not yet approved by admin
      if (
        team.statusCheckpoints?.submit_approval?.completed &&
        !team.statusCheckpoints?.confirmed_approval?.completed
      ) {
        return "awaiting_admin";
      }

      // Case 6: Approved by admin - ready for payment
      if (team.statusCheckpoints?.confirmed_approval?.completed) {
        return "payment";
      }
    }

    // Default case
    return "invite";
  };

  const renderStatusComponent = () => {
    const status = getCurrentTeamStatus();

    switch (status) {
      case "invite":
        return <InviteTeamMateStatus team={team!} onUpdate={loadTeamDetails} />;
      case "confirm":
        return (
          <ConfirmTeamMateStatus team={team!} onUpdate={loadTeamDetails} />
        );
      case "waiting":
        return (
          <AwaitingConfirmationStatus team={team!} onUpdate={loadTeamDetails} />
        );
      case "terms":
        return (
          <SignTermsConditionsStatus team={team!} onUpdate={loadTeamDetails} />
        );
      case "submit":
        return (
          <SubmitForApprovalStatus team={team!} onUpdate={loadTeamDetails} />
        );
      case "awaiting_admin":
        return (
          <AwaitingAdminApprovalStatus
            team={team!}
            onUpdate={loadTeamDetails}
          />
        );
      case "payment":
        return (
          <ReadyForPaymentStatus team={team!} onUpdate={loadTeamDetails} />
        );
      default:
        return <InviteTeamMateStatus team={team!} onUpdate={loadTeamDetails} />;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !team) {
    return (
      <DashboardLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {error || "Team not found"}
        </div>
      </DashboardLayout>
    );
  }

  const checklistItems = getChecklistItems();
  console.log("checklistItems", checklistItems);

  return (
    <DashboardLayout>
      <div className="bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1">
            {/* Team Header */}
            <div className="w-full">
              <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4 gap-4">
                {/* Team Name and ID - Full width and centered on mobile */}
                <div className="w-full md:w-auto text-center md:text-left">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    {team.team_name}
                  </h1>
                  <p className="text-gray-500 font-mono text-sm tracking-wider">
                    {team.team_id}
                  </p>
                </div>
                {/* Time Info - Full width and centered on mobile */}
                <div className="w-full md:w-auto text-center md:text-right">
                  {/* <p className="text-lg font-semibold text-gray-900">
                    4 Days 12 hours left
                  </p>
                  <p className="text-sm text-gray-600">
                    To claim Early Bird discounts
                  </p> */}
                </div>
              </div>

              {/* Team Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Created by
                  </label>
                  <input
                    type="text"
                    value={
                      team.members.find((m) => m.member_position === "1")
                        ?.full_name || ""
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    League
                  </label>
                  <input
                    type="text"
                    value={team.league.name}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Joined by
                  </label>
                  <input
                    type="text"
                    value={
                      team.members.find((m) => m.member_position === "2")
                        ?.full_name +
                        (team.member_count < 2
                          ? " (Confirmation Required)"
                          : "") || "Waiting for team member..."
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Category
                  </label>
                  <input
                    type="text"
                    value={team.category.name}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    readOnly
                  />
                </div>
              </div>
            </div>
            {/* Separator Line */}
            <hr className="w-full border-gray-400 my-2" />
            {/* Checklist & Status Section */}
            <div className="w-full">
              <div className="flex flex-col lg:flex-row gap-6 lg:items-stretch">
                {/* Left: Checklist */}
                <div className="w-full lg:w-1/2 flex flex-col">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 text-left">
                    Checklist
                  </h2>
                  <div className="flex-1 space-y-3">
                    {checklistItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center space-x-3"
                      >
                        <div className="w-5 h-5 flex-shrink-0">
                          {item.completed ? (
                            <img
                              src={CheckedImg}
                              alt="Completed"
                              className="w-full h-full"
                            />
                          ) : (
                            <img
                              src={UnCheckedImg}
                              alt="Not completed"
                              className="w-full h-full"
                            />
                          )}
                        </div>
                        <span
                          className={`text-sm ${
                            item.completed ? "text-gray-900" : "text-red-600"
                          }`}
                        >
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right: Status Component */}
                <div className="w-full lg:w-1/2 flex flex-col">
                  <div className="flex-1">{renderStatusComponent()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TeamDetailsPage;
